import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppNavigate, useAppParams, useAppSelector, usePermissions } from "@app/hooks";
import { useContextAwareGetActivityById, useContextAwareGetModulesByActivityId, useContextAwareGetProjectById, useContextAwareGetLandUseChangeByActivityId, useContextAwareGetModuleTypes } from "@hooks/index";
import { selectCurrentProject, setActivity, setProject } from "@features/project/projectSlice";
import { BEModulesClassNames, getFEModuleListFromActivity } from "@utils/moduleList";
import { useEffect, useMemo } from "react";
import { setMainView, setModuleList } from "@features/builder/builderSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { encodeActivityId } from "@utils/projectIdEncoder";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";

export const useHandleActivityState = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { isPublic } = usePermissions();
  const { pathname } = useLocation();
  const { activity, project } = useAppSelector(selectCurrentProject);
  const { activityId } = useAppParams();
  const id = useMemo(() => Number(activityId), [activityId]);
  const isActivityIdMismatch = id && activity?.id ? id !== activity?.id : false;

  const { data: activityData, isLoading, isFetching, isError, error } = useContextAwareGetActivityById(Number(activityId) ?? 0, { skip: activityId === activity?.id });
  const { data: modulesData, isLoading: isLoadingModules } = useContextAwareGetModulesByActivityId(Number(activityId) ?? 0, { skip: !activityId });
  const { data: projectData } = useContextAwareGetProjectById(activityData?.project?.toString() ?? "", { skip: !activityData?.project });
  // Mapping BE module types to FE modules goes through the ModuleTypeRegistry,
  // which is only filled once /module-types/ has loaded (`moduleTypes` defined).
  // Building the module list before that drops every module (uniqueId 0, empty
  // step bar), so everything below waits for it. Same cache entry as Provider's.
  const { data: moduleTypes, isLoading: isLoadingModuleTypes } = useContextAwareGetModuleTypes({});

  // Determine whether the activity has a land-use-change (LUC) module.
  // The FE module list ordering depends on the LUC's start/with/without module
  // types, so we must fetch the LUC data before we can build it correctly.
  const hasLucModule = useMemo(() => {
    if (!modulesData || !moduleTypes) return false;
    const registry = ModuleTypeRegistry.getInstance();
    const landUseChangeId = registry.getIdByClassName(BEModulesClassNames.LandUseChange);
    return modulesData.some(mod => mod.module_type === landUseChangeId);
  }, [modulesData, moduleTypes]);

  const { data: lucData, isLoading: isLoadingLuc } = useContextAwareGetLandUseChangeByActivityId(
    { activityId: Number(activityId) },
    { skip: !hasLucModule || !activityId }
  );

  // Tier 2 is a per-activity choice: opening a different activity starts on Tier 1.
  // Compared against the persisted activity, so a refresh on the same one keeps Tier 2.
  useEffect(() => {
    if (id && activity?.id !== id) dispatch(setMainView("tier1"));
  }, [id, activity?.id, dispatch]);

  // Effect 1 — activity/module sync + initial redirect.
  // Runs once activity, modules, module types (and optionally LUC) data are all available.
  useEffect(() => {
    if (id && activityData && activityData.id === id && modulesData && moduleTypes) {
      // LUC sync gate: if there is a LUC module but its data hasn't loaded
      // yet, bail out and wait for the next render. Without this guard we
      // would build the FE module list before knowing the LUC module types,
      // producing an incorrect ordering.
      if (hasLucModule && isLoadingLuc) return;

      const feModuleList = getFEModuleListFromActivity(
        modulesData,
        lucData?.module ? {
          module_type_start: lucData.module.module_type_start,
          module_type_w: lucData.module.module_type_w,
          module_type_wo: lucData.module.module_type_wo,
        } : undefined
      );
      dispatch(setActivity(activityData));
      dispatch(setModuleList(feModuleList));

      // Redirect rule: if the user landed on the bare builder route (no
      // module sub-path), navigate to the first module automatically.
      // Private route example: /builder/42        → splitPath.length === 2
      // Public route example:  /builder/enc42/pub → splitPath.length === 3
      // Any deeper path already has a module selected — leave it alone.
      const splitPath = pathname.split("/").filter(path => path !== "");
      if (splitPath.length <= (isPublic ? 3 : 2)) {
        // Public routes use encoded activity IDs to prevent enumeration.
        // TODO(Phase 6): extract this encoding fallback — it is duplicated
        // verbatim in GenericModule.tsx handleNext().
        const activityIdToUse = isPublic
          ? (() => {
            try {
              return encodeActivityId(id);
            } catch (error) {
              console.error('Failed to encode activity ID:', error);
              return id.toString();
            }
          })()
          : id;

        const basePath = `/builder/${activityIdToUse}`;
        const targetPath = feModuleList.length > 0
          ? `${basePath}/${feModuleList[0].path}`
          : `${basePath}/complete`;

        navigate({ path: targetPath });
      }
    }
  }, [activityData, modulesData, moduleTypes, id, dispatch, pathname, isPublic, navigate, hasLucModule, isLoadingLuc, lucData]);

  // Effect 2 — project sync.
  // Kept separate from Effect 1 because projectData arrives later (it depends
  // on activityData.project) and the two pieces of Redux state are independent.
  useEffect(() => {
    if (projectData && project?.id !== projectData.id) {
      dispatch(setProject(projectData));
    }
  }, [project, dispatch, projectData]);

  return { activity, isLoading: isLoading || isLoadingModules || isLoadingModuleTypes || isLoadingLuc || isActivityIdMismatch, isFetching, isError, error: error as FetchBaseQueryError };
}